'use client';

import { useEffect } from 'react';
import sha256 from 'crypto-js/sha256';
import FBSettingsService from '@/components/default/services/api/dashboard/fb.service';

declare global {
    interface Window {
        fbq?: (...args: any[]) => void;
        _fbq?: any;
        sendFBEvent?: (eventName: string, eventData?: Record<string, any>, userEmail?: string) => void;
    }
}

type PixelProps = {
    pixelData: {
        meta_pixel_id: string;
        // meta_catalog_id?: string; // optional for product events
    };
};

export default function MetaPixelClient({ pixelData }: PixelProps) {
    useEffect(() => {
        const pixelId = pixelData.meta_pixel_id;
        if (!pixelId) return;

        // Prevent double loading
        if (window.fbq) return;

        // Initialize fbq function
        const fbqFunc = function (...args: any[]) {
            if (typeof fbqFunc.callMethod === 'function') {
                fbqFunc.callMethod.apply(fbqFunc, args);
            } else {
                fbqFunc.queue.push(args);
            }
        } as any;

        fbqFunc.queue = [] as any[];
        fbqFunc.loaded = true;
        fbqFunc.version = '2.0';

        window.fbq = fbqFunc;
        window._fbq = fbqFunc;

        // Load Meta Pixel script
        const script: HTMLScriptElement = document.createElement('script');
        script.async = true;
        script.src = 'https://connect.facebook.net/en_US/fbevents.js';
        const firstScript = document.getElementsByTagName('script')[0];
        firstScript?.parentNode?.insertBefore(script, firstScript);

        // Initialize Pixel & track PageView
        window.fbq?.('init', pixelId);
        window.fbq?.('track', 'PageView');

        // -----------------------------
        // Global helper to send events
        // -----------------------------
        window.sendFBEvent = async (eventName: string, eventData: Record<string, any> = {}, userEmail?: string) => {
            // 1️⃣ Client-side Pixel
            window.fbq?.('track', eventName, eventData);

            // 2️⃣ Server-side CAPI
            try {
                const payload: any = {
                    event_name: eventName,
                    event_data: {
                        user_data: {},
                        custom_data: eventData,
                    },
                    // catalog_id: pixelData.meta_catalog_id ?? '', // optional for product events
                };

                if (userEmail) {
                    payload.event_data.user_data.em = sha256(userEmail).toString();
                }

                await FBSettingsService.sendCAPIEvent(payload);
            } catch (err) {
                console.error('CAPI event error:', err);
            }
        };
    }, [pixelData]);

    return null;
}
